import * as Sentry from '@sentry/react';
import { backendApi } from '_config';
import { IP_LOCATION_ENDPOINT } from '_services/Api/IPLocation/IPLocationApi';
import { xsrfTokenRequestInterceptor } from '_utils/xsrfInterceptor';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import Csrf from '../Csrf';
import RequestLimitter from './RequestLimitter';

// Create a separate axios instance to avoid circular dependencies
const ApiAxios = axios.create({
    baseURL: backendApi,
    headers: {
        'Content-type': 'application/json',
    },
    withCredentials: true,
});

let cachedClientIp: string | null | undefined;
let clientIpPromise: Promise<string | null> | null = null;

const LOCAL_STORAGE_KEY = 'knitandnote:client-ip';
const IP_CACHE_TTL_MS = 5 * 24 * 60 * 60 * 1000; // 5 days

const readCachedIp = (): string | null => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as { ip?: string; ts?: number };
        if (!parsed.ip || typeof parsed.ip !== 'string') return null;
        if (!parsed.ts || typeof parsed.ts !== 'number') return null;

        const isFresh = Date.now() - parsed.ts < IP_CACHE_TTL_MS;
        return isFresh ? parsed.ip : null;
    } catch {
        return null;
    }
};

const persistCachedIp = (ip: string) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    try {
        window.localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({ ip, ts: Date.now() }),
        );
    } catch {
        // Best-effort cache; ignore storage errors (e.g., quota, Safari private mode)
    }
};

const fetchClientIp = async (): Promise<string | null> => {
    // Browser-only: skip when rendering on the server or if fetch is unavailable
    if (typeof window === 'undefined' || typeof fetch === 'undefined') {
        return null;
    }

    if (cachedClientIp) return cachedClientIp;

    const storedIp = readCachedIp();
    if (storedIp) {
        cachedClientIp = storedIp;
        return cachedClientIp;
    }

    if (!clientIpPromise) {
        clientIpPromise = fetch(IP_LOCATION_ENDPOINT)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                const ip = (data as { ip?: string })?.ip;
                return typeof ip === 'string' && ip ? ip : null;
            })
            .catch(() => null); // swallow failures; IP headers are best-effort
    }

    const ip = await clientIpPromise;
    if (ip) {
        cachedClientIp = ip;
        persistCachedIp(ip);
    } else {
        // allow retry on subsequent requests if the lookup failed
        clientIpPromise = null;
    }
    return ip;
};

ApiAxios.interceptors.request.use(async (request) => {
    const requestWithXsrf = xsrfTokenRequestInterceptor(request);

    try {
        const clientIp = await fetchClientIp();
        if (clientIp) {
            requestWithXsrf.headers = {
                ...(requestWithXsrf.headers ?? {}),
                'CF-Connecting-IP': clientIp,
                'X-Forwarded-For': clientIp,
            };
        }
    } catch (error) {
        console.warn('Skipping client IP header', error);
    }

    return requestWithXsrf;
});

const DEFAULT_TIMEOUT_MS = 15000;
const RETRY_DELAYS_MS = [400, 1200]; // jitter will be added per-attempt

export type AxiosRequestShape = {
    url: string;
    method?: AxiosRequestConfig['method'];
    body?: AxiosRequestConfig['data'];
    params?: AxiosRequestConfig['params'];
    headers?: AxiosRequestConfig['headers'];
    timeout?: AxiosRequestConfig['timeout'];
};

function ensureValidUrl(url: string): string | undefined {
    let thisUrl: string;
    try {
        thisUrl = new URL(url).toString();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
        /**
         * Url may come in with base or without. For example it may be countries/list,
         * or it may be http(s)://{domain}/some/stuff
         * This logic ensures that backend api is used in the first instance
         */
        thisUrl = backendApi + url;
    }

    try {
        /**
         * After we've ensured baseUrl, we try again URL constructor validation, just in case
         */
        thisUrl = new URL(thisUrl).toString();
    } catch (e) {
        /**
         * If this fail now, we display an error because definetly there's something wrong
         */
        console.error('Invalid url used for api call', { url, e });
        return undefined;
    }

    return thisUrl;
}

export const callWithRequestLimiter = async ({
    url,
    method = 'GET',
    body,
    params,
    headers,
    timeout,
}: AxiosRequestShape): Promise<AxiosResponse> => {
    try {
        /**
         * Setting get jobs and remove jobs globally
         * To remove when unmounting of Social Component
         */
        if (!window?.removeRequestLimitterJobs) {
            window.removeRequestLimitterJobs = RequestLimitter.removeRequests;
        }

        return await RequestLimitter.pushRequest(async () => {
            await Csrf.ensureReady();
            let csrfCookie = await Csrf.getCookie();
            if (typeof csrfCookie !== 'string' || !csrfCookie) {
                // Attempt to create cookie once, then re-check
                try {
                    await Csrf.createCsrfCookie();
                    csrfCookie = await Csrf.getCookie();
                } catch {}
                if (typeof csrfCookie !== 'string' || !csrfCookie) {
                    return Promise.reject(
                        new Error('Invalid CSRF cookie: ' + String(csrfCookie)),
                    );
                }
            }
            const thisUrl = ensureValidUrl(url);
            if (!thisUrl) {
                return Promise.reject(
                    new Error('Invalid URL for call: ' + String(url)),
                );
            }
            const perRequestTimeout = timeout ?? DEFAULT_TIMEOUT_MS;
            const start = performance.now();
            const requestId = `API-${method}-${thisUrl}`;
            const jitter = () => Math.floor(Math.random() * 150);
            const sleep = (ms: number) =>
                new Promise((resolve) => setTimeout(resolve, ms));

            const isRetryable = (err: AxiosError) => {
                const status = err.response?.status;
                const code = err.code;
                if (code === 'ECONNABORTED' || code === 'ERR_CANCELED') {
                    return true;
                }
                if (!status) {
                    return true; // network errors / no response
                }
                return status >= 500 && status !== 501;
            };

            const runOnce = async (attempt: number): Promise<AxiosResponse> => {
                try {
                    return await ApiAxios({
                        url: thisUrl,
                        method,
                        data: body,
                        params,
                        headers,
                        timeout: perRequestTimeout,
                    });
                } catch (err: unknown) {
                    // If CSRF mismatch (419), refresh cookie and retry once
                    if (
                        err instanceof AxiosError &&
                        (err.response?.status === 419 ||
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (err.response?.status as any as string) ===
                                '419') &&
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        !(err.config && (err.config as any)._csrfRetried)
                    ) {
                        try {
                            Sentry.addBreadcrumb({
                                category: 'csrf',
                                level: 'info',
                                message:
                                    'CSRF 419 in RequestLimiter: refreshing cookie and retrying',
                                data: { url: thisUrl, method, attempt },
                            });
                            await Csrf.createCsrfCookie();
                            const retryConfig = {
                                url: thisUrl,
                                method,
                                data: body,
                                params,
                                headers,
                                timeout: perRequestTimeout,
                                _csrfRetried: true as unknown as undefined,
                            } as AxiosRequestConfig & {
                                _csrfRetried?: boolean;
                            };
                            const result = await ApiAxios(retryConfig);
                            Sentry.addBreadcrumb({
                                category: 'csrf',
                                level: 'info',
                                message: 'CSRF 419 retry succeeded',
                                data: { url: thisUrl, method, attempt },
                            });
                            return result;
                        } catch (retryErr) {
                            Sentry.addBreadcrumb({
                                category: 'csrf',
                                level: 'error',
                                message: 'CSRF 419 retry failed',
                                data: { url: thisUrl, method, attempt },
                            });
                            throw retryErr;
                        }
                    }
                    throw err;
                }
            };

            for (
                let attempt = 0;
                attempt <= RETRY_DELAYS_MS.length;
                attempt++
            ) {
                try {
                    const response = await runOnce(attempt + 1);
                    const durationMs = Math.round(performance.now() - start);
                    Sentry.addBreadcrumb({
                        category: 'api-request',
                        level: 'info',
                        message: 'api-request-success',
                        data: {
                            requestId,
                            url: thisUrl,
                            method,
                            durationMs,
                            attempt: attempt + 1,
                            status: response.status,
                        },
                    });
                    return response;
                } catch (err: unknown) {
                    const axiosErr = err as AxiosError;
                    const durationMs = Math.round(performance.now() - start);
                    const abortedReason =
                        axiosErr?.message?.includes('timeout') ||
                        axiosErr?.code === 'ECONNABORTED' ||
                        axiosErr?.code === 'ERR_CANCELED';
                    Sentry.addBreadcrumb({
                        category: 'api-request',
                        level: 'warning',
                        message: 'api-request-failed',
                        data: {
                            requestId,
                            url: thisUrl,
                            method,
                            durationMs,
                            attempt: attempt + 1,
                            status: axiosErr?.response?.status,
                            code: axiosErr?.code,
                            aborted: abortedReason,
                        },
                    });

                    if (
                        axiosErr instanceof AxiosError &&
                        attempt < RETRY_DELAYS_MS.length &&
                        isRetryable(axiosErr)
                    ) {
                        const delay = RETRY_DELAYS_MS[attempt] + jitter();
                        Sentry.addBreadcrumb({
                            category: 'api-request',
                            level: 'info',
                            message: 'api-request-retry',
                            data: {
                                requestId,
                                url: thisUrl,
                                method,
                                nextDelayMs: delay,
                                nextAttempt: attempt + 2,
                            },
                        });
                        await sleep(delay);
                        continue;
                    }
                    throw err;
                }
            }

            throw new Error('Exceeded retry attempts for ' + thisUrl);
        }, `API-${method}-${url}`);
    } catch (e) {
        const err = e as AxiosError & {
            code?: string;
            isHandled?: boolean;
            reason?: string;
            route?: string;
            queueSize?: number;
            oldestAgeMs?: number;
        };
        const msg = (err.message || '').toLowerCase();
        const isCancelled =
            err.code === 'ERR_CANCELED' ||
            err.code === 'ECONNABORTED' ||
            msg.includes('canceled') ||
            msg.includes('cancelled') ||
            msg.includes('aborted');

        // Skip noisy queue-flush cancellations (user navigated away) and browser aborts
        if (err.code === 'QUEUE_FLUSHED' || isCancelled) {
            const abortReason =
                err.reason ?? (isCancelled ? 'browser-abort' : 'queue-flush');
            Sentry.addBreadcrumb({
                category: 'request-abort',
                level: 'info',
                message: abortReason,
                data: {
                    url,
                    method,
                    abort_reason: abortReason,
                    queueSize: err.queueSize,
                    oldestAgeMs: err.oldestAgeMs,
                    route: err.route,
                },
            });
            return Promise.reject(
                Object.assign(err, {
                    isHandled: true,
                    abort_reason: abortReason,
                }),
            );
        }

        if (
            err instanceof AxiosError &&
            err.response?.status !== 401 &&
            err.status !== 401 &&
            err.status !== 419 &&
            err.code !== 'ERR_NETWORK'
        ) {
            Sentry.captureException(err);
        }
        throw err;
    }
};
